import type { MemoryGroup, VariousMemoryIdRefs } from '@timelyapp/integration-cloud-api-spec';
import { EMPTY_ARRAY } from 'constants/defaults';
import { SHOW_MEMORY_DRAWER_CONTENT } from 'constants/user';
import { isEqual } from 'lodash-es';
import { DateTime } from 'luxon';
import {
  getTicAndLegacyMemories,
  getTicAndLegacyMemoriesAsMemoryGroups,
} from 'redux/modules/tic/tic.memories.selectors';
import { createSelector } from 'reselect';
import type {
  EnrichedHourTimestamp,
  EnrichedTimestampsWithSummary,
  MemoryDrawerProps,
} from 'types/memoryDrawer';
import { keyBy } from 'utils/typesafe';
import { canAutoUpdateDraftDuration } from './hours.draft';
import { getHourDraft } from './hours.draft.base';
import { getUser } from './user';

const getHourDraftTimestamps = createSelector([getHourDraft], draft => draft?.timestamps);

/**
 * Get all memories related to Hour's `timestamps`, then enrich them with
 * context to the (explicit, indirect, or partially) associated memories.
 */
export const getEnrichedHourDraftTimestamps = createSelector(
  [getTicAndLegacyMemoriesAsMemoryGroups, getTicAndLegacyMemories, getHourDraftTimestamps],
  (memoryGroups, memories, draftTimestamps): EnrichedTimestampsWithSummary => {
    if (!draftTimestamps)
      return {
        timestamps: EMPTY_ARRAY,
        summary: {
          total_seconds: 0,
          num_memories: 0,
          num_memories_checked: 0,
        },
      };

    const groupsById = keyBy(memoryGroups, 'id');
    const groupsByVid = keyBy(memoryGroups, 'v_id');
    const memoriesById = keyBy(memories, 'id');

    const timestamps = draftTimestamps.map(timestamp => {
      const timestampFrom = DateTime.fromISO(timestamp.from).toUTC();
      const timestampTo = DateTime.fromISO(timestamp.to).toUTC();

      const entryIds = timestamp.entry_ids as Array<unknown>;
      const selectedMemories = entryIds.flatMap(id_ => {
        const fromGroup = (group: MemoryGroup) => ({
          id: group.id,
          v_id: group.v_id!,
          title: group.title,
          total_seconds: group.total_seconds,
          icon_url: group.icon_url || null,
          tool_id: group.tool?.id || null,
          is_checked: true,
        });
        const id = String(id_);
        const group = groupsById[id];
        if (group) {
          return fromGroup(group);
        }

        const memory = memoriesById[id];
        if (memory) {
          return {
            id: memory.id,
            v_id: `${memory.id}` as VariousMemoryIdRefs,
            title: memory.title,
            total_seconds: memory.total_seconds,
            icon_url: memory.icon_url || null,
            tool_id: memory.tool?.id || null,
            is_checked: true,
          };
        }

        const groupByVid = groupsByVid[id];
        if (groupByVid) {
          return fromGroup(groupByVid);
        }

        const splitIdx = id.lastIndexOf('_');
        if (splitIdx === -1) return [];

        const idPart = id.slice(splitIdx + 1);
        const memoryByVid = memoriesById[idPart];
        if (memoryByVid)
          return {
            id: memoryByVid.id,
            v_id: id as VariousMemoryIdRefs,
            title: memoryByVid.title,
            total_seconds: memoryByVid.total_seconds,
            icon_url: memoryByVid.icon_url || null,
            tool_id: memoryByVid.tool?.id || null,
            is_checked: true,
          };

        return [];
      });
      selectedMemories.sort((a, b) => b.total_seconds - a.total_seconds);

      const indirectlySelectedGroups = memoryGroups
        .filter(memory => {
          if (selectedMemories.some(m => m.id === memory.id)) return false;
          if (selectedMemories.some(m => (m as any as MemoryGroup).v_id === memory.v_id))
            return false;

          // If indirectly logged via timestamp, include it
          const memoryStart = DateTime.fromISO(memory.started_at).toUTC();
          const memoryEnd = DateTime.fromISO(memory.ended_at).toUTC();
          return memoryStart >= timestampFrom && memoryEnd <= timestampTo;

          // Partially logged via timestamps _ignored_
        })
        .map(memory => ({
          id: memory.id,
          v_id: memory.v_id!,
          title: memory.title,
          total_seconds: memory.total_seconds,
          icon_url: memory.icon_url || null,
          tool_id: memory.tool?.id || null,
          is_checked: false,
        }));
      const memory_associations = selectedMemories
        .concat(indirectlySelectedGroups)
        .sort((a, b) => b.total_seconds - a.total_seconds);

      const total_seconds = timestampTo.diff(timestampFrom, 'seconds').seconds;

      // Add meta data directly to each timestamp
      const MAX_ICONS = 3;

      const icons = selectedMemories.slice(0, MAX_ICONS).map(({ icon_url }) => ({ icon_url }));

      const iconsCount =
        selectedMemories.length > MAX_ICONS ? selectedMemories.length - MAX_ICONS : null;

      const meta = {
        icons,
        iconsCount,
        title: '',
      };

      return {
        ...timestamp,
        memory_associations,
        total_seconds,
        meta,
      } satisfies EnrichedHourTimestamp;
    });

    const allMemories = timestamps.flatMap(t => t.memory_associations);

    return {
      timestamps,
      summary: {
        total_seconds: timestamps.reduce((total, t) => total + t.total_seconds, 0),
        num_memories: allMemories.length,
        num_memories_checked: allMemories.filter(m => m.is_checked).length,
      },
    };
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);

const getShowExpandedMemoryDrawer = createSelector(
  [getUser],
  (user): boolean => user.properties[SHOW_MEMORY_DRAWER_CONTENT] === true
);

export const getTicMemoryDrawerProps = createSelector(
  [getEnrichedHourDraftTimestamps, getShowExpandedMemoryDrawer, canAutoUpdateDraftDuration],
  ({ timestamps, summary }, isExpanded, isAutoUpdated): MemoryDrawerProps => {
    return {
      timestamps,
      summary,
      ui: { isExpanded, isAutoUpdated },
    };
  }
);
